import { useState } from 'react';
import { Card } from '@agro1desenvolvimento/react-components';
import GerenciadorImagens from '../../GerenciadorImagens/GerenciadorImagens';
import tetrazolioService, { ImagemTetrazolio } from '../../../../../../services/sementes/tetrazolio';
import ModalDecisao from '../../Modais/ModalDecisao';
import ModalConfirmacao from '../../Modais/ModalConfirmacao';
import { AnaliseStatus } from '../../../../../../@types/sementes/analiseStatus';
import { formatDateBR } from '../../../../../../utilities/date';

interface DadosAnaliseInicial {
  data: Date;
  solucao: string;
  repeticoes: number;
  sementesPorRepeticao: number;
}

interface Props {
  produtor: string;
  idDaAmostra: string;
  numeroRemessa: string;
  especie: string;
  lote: string;
  safra: string;
  nomeCientifico: string;
  cultivar: string;
  categoria: string;
  dataEntrada: Date | null;
  dataAmostragem: Date | null;
  status: AnaliseStatus;
  concluidoApi?: boolean;
  dadosAnaliseInicial?: DadosAnaliseInicial;
  onCancelarAnalise?: () => void | Promise<void>;
  imagens?: ImagemTetrazolio[];
  onAdicionarImagem?: (imagem: Omit<ImagemTetrazolio, 'id'>) => Promise<void>;
  onRemoverImagem?: (imagemId: string) => Promise<void>;
  loadingImagens?: boolean;
  analiseId: string;
}

export default function FormularioTetrazolioHeader({
  produtor,
  idDaAmostra,
  numeroRemessa,
  especie,
  lote,
  safra,
  nomeCientifico,
  cultivar,
  categoria,
  dataEntrada,
  dataAmostragem,
  status,
  concluidoApi = false,
  dadosAnaliseInicial,
  onCancelarAnalise,
  imagens = [],
  onAdicionarImagem,
  onRemoverImagem,
  loadingImagens = false,
  analiseId,
}: Props) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [modalCancelarVisible, setModalCancelarVisible] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [modalErroVisible, setModalErroVisible] = useState(false);

  const handleAdicionarImagem = (imagem: any) => {
    if (onAdicionarImagem) {
      const imagemTetrazolio: Omit<ImagemTetrazolio, 'id'> = {
        ...imagem,
        timestamp: new Date(),
      };
      onAdicionarImagem(imagemTetrazolio);
    }
  };

  const handleCancelarAnalise = () => {
    setModalCancelarVisible(true);
  };

  const confirmarCancelamento = async () => {
    try {
      setCancelando(true);

      await tetrazolioService.cancelarAnalise(analiseId);

      if (onCancelarAnalise) {
        await onCancelarAnalise();
      }

      setModalCancelarVisible(false);
    } catch (error) {
      console.error('Erro ao cancelar análise:', error);
      setModalErroVisible(true);
    } finally {
      setCancelando(false);
    }
  };

  const getStatusClass = () => {
    if (status.toLowerCase() === 'iniciado') {
      return 'iniciado';
    } if (status.toLowerCase() === 'realizado') {
      return 'realizado';
    } if (status.toLowerCase() === 'pendente') {
      return 'pendente';
    }
    return 'pendente';
  };

  return (
    <Card className="formulario-tetrazolio-header">
      <div className="header">
        <h2 className="title">Amostra - {idDaAmostra}</h2>
        <span className={`status ${getStatusClass()}`}>{status}</span>
      </div>

      {!isCollapsed && (
        <div className="grid">
          <div className="field">
            <label>Requerente</label>
            <span>{produtor}</span>
          </div>
          <div className="field">
            <label>N° da Remessa</label>
            <span>{numeroRemessa}</span>
          </div>
          <div className="field">
            <label>Espécie</label>
            <span>{especie}</span>
          </div>
          <div className="field">
            <label>Lote</label>
            <span>{lote}</span>
          </div>
          <div className="field">
            <label>Safra</label>
            <span>{safra}</span>
          </div>
          <div className="field">
            <label>Nome Científico</label>
            <span>{nomeCientifico}</span>
          </div>
          <div className="field">
            <label>Cultivar</label>
            <span>{cultivar}</span>
          </div>
          <div className="field">
            <label>Categoria</label>
            <span>{categoria}</span>
          </div>
          <div className="field">
            <label>Data de Entrada</label>
            <span>{formatDateBR(dataEntrada)}</span>
          </div>
          <div className="field">
            <label>Data de Amostragem</label>
            <span>{formatDateBR(dataAmostragem)}</span>
          </div>
        </div>
      )}

      {!isCollapsed && onAdicionarImagem && onRemoverImagem && (
        <GerenciadorImagens
          imagens={imagens}
          onAdicionarImagem={handleAdicionarImagem}
          onRemoverImagem={onRemoverImagem}
          loading={loadingImagens}
          maxImagens={10}
        />
      )}

      {!isCollapsed && dadosAnaliseInicial && (
        <div className="analysis-card">
          <div className="analysis-grid">
            <div className="analysis-field">
              <label>Data da Análise</label>
              <span>{formatDateBR(dadosAnaliseInicial.data)}</span>
            </div>
            <div className="analysis-field">
              <label>Solução</label>
              <span>{dadosAnaliseInicial.solucao}</span>
            </div>
            <div className="analysis-field">
              <label>Repetições</label>
              <span>{dadosAnaliseInicial.repeticoes}</span>
            </div>
            <div className="analysis-field">
              <label>Sementes por Repetição</label>
              <span>{dadosAnaliseInicial.sementesPorRepeticao}</span>
            </div>
          </div>
          {!concluidoApi && (
            <button
              type="button"
              className="cancel-button"
              onClick={handleCancelarAnalise}
              disabled={cancelando}
            >
              {cancelando ? 'Cancelando...' : 'Cancelar'}
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        className={`hide-button ${isCollapsed ? 'collapsed' : ''}`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`} />
        Esconder
      </button>

      <ModalDecisao
        visible={modalCancelarVisible}
        title="Cancelar análise?"
        description="Todos os dados da análise serão removidos e não poderão ser recuperados. Esta ação não pode ser desfeita. Deseja continuar?"
        okButtonLabel="Sim, cancelar análise"
        cancelButtonLabel="Não"
        onConfirm={confirmarCancelamento}
        onCancel={() => setModalCancelarVisible(false)}
      />

      <ModalConfirmacao
        visible={modalErroVisible}
        title="Erro ao cancelar"
        description="Ocorreu um erro ao tentar cancelar a análise. Por favor, tente novamente."
        buttonLabel="Fechar"
        onConfirm={() => setModalErroVisible(false)}
      />
    </Card>
  );
}
